import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isTeacher, isPartnerAdminDashboard } from '../../utils/authScopes';
import { appNamedRoutes } from '../../routes';
import UnreadMessagesBell from './UnreadMessagesBell';
import SUPPORTED_LOCALES from '../../utils/supportedLocales';
import SparklerLogo from '../../../assets/images/sparkler-logo.svg';

const LanguageMenu = ({ onSaveLocale, i18n, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const currentLang = SUPPORTED_LOCALES.find(l => l.code === i18n.language) || SUPPORTED_LOCALES[0];

  return (
    <div className="o-navbar__profile" ref={ref}>
      <button className="o-navbar__profile-btn" onClick={() => setOpen(o => !o)} aria-label={t('nav.language')}>
        <svg className="o-navbar__profile-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
        <span className="o-navbar__lang-label">{currentLang.code.toUpperCase()}</span>
      </button>
      {open && (
        <div className="o-navbar__profile-menu">
          <div className="o-navbar__profile-section-label">{t('nav.language')}</div>
          {SUPPORTED_LOCALES.map(({ code, label }) => (
            <button
              key={code}
              className={`o-navbar__profile-item${code === i18n.language ? ' is-active' : ''}`}
              onClick={() => {
                const previous = i18n.language;
                if (code === previous) { setOpen(false); return; }
                i18n.changeLanguage(code);
                onSaveLocale(code, previous);
                setOpen(false);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProfileMenu = ({ auth, onLogout, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const history = useHistory();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="o-navbar__profile" ref={ref}>
      <button className="o-navbar__profile-btn" onClick={() => setOpen(o => !o)} aria-label={t('nav.profileMenu')}>
        <svg className="o-navbar__profile-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
        </svg>
      </button>
      {open && (
        <div className="o-navbar__profile-menu">
          <div className="o-navbar__profile-header">
            <div className="o-navbar__profile-name">{auth.name}</div>
            {auth.email && <div className="o-navbar__profile-email">{auth.email}</div>}
          </div>
          <div className="o-navbar__profile-divider" />
          <button
            className="o-navbar__profile-item"
            onClick={() => { history.push(appNamedRoutes.settings); setOpen(false); }}
          >
            {t('nav.changePassword')}
          </button>
          <div className="o-navbar__profile-divider" />
          <button
            className="o-navbar__profile-item o-navbar__profile-item--danger"
            onClick={() => { onLogout(); setOpen(false); }}
          >
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
};

const isLightColor = (hex) => {
  if (!hex) return false;
  const c = hex.replace('#', '');
  const r = parseInt(c.length === 3 ? c[0] + c[0] : c.slice(0, 2), 16);
  const g = parseInt(c.length === 3 ? c[1] + c[1] : c.slice(2, 4), 16);
  const b = parseInt(c.length === 3 ? c[2] + c[2] : c.slice(4, 6), 16);
  // perceived luminance
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
};

const FixedBar = ({ auth, onLogout, onStartASQ, onSaveLocale }) => {
  const { t, i18n } = useTranslation();
  const { bgColor, isAuthenticated, logoUrl, name } = auth;
  if (!isAuthenticated) return null;
  const style = bgColor ? { backgroundColor: bgColor } : {};
  const lightBg = isLightColor(bgColor);
  const rootUrl = isPartnerAdminDashboard(auth) ? appNamedRoutes.partnerRoot : appNamedRoutes.asqs;
  return (
    <div className={`o-navbar cs-bg--sp-black${lightBg ? ' o-navbar--light-bg' : ''}`} style={style}>
      <div className="o-navbar__wrap row align-middle">
        <div className="column u-flex align-middle">
          <Link to={{pathname: rootUrl, state: {from: 'navbar'} }} className="o-navbar__logo">
            <img alt='Sparkler' src={logoUrl || SparklerLogo} />
          </Link>
          <div className='u-txt--nav-name u-padding-left--gutter'>{name}</div>
        </div>
        <div className="column shrink u-flex align-middle justify-right">
          { isTeacher(auth) && <a className="o-sp-button o-sp-button--base o-sp-button--primary" onClick={onStartASQ}>{t('nav.startAsq')}</a> }
          <UnreadMessagesBell auth={auth} t={t} />
          <LanguageMenu onSaveLocale={onSaveLocale} i18n={i18n} t={t} />
          <ProfileMenu auth={auth} onLogout={onLogout} t={t} />
        </div>
      </div>
    </div>
  );
}

FixedBar.propTypes = {
  auth: PropTypes.object.isRequired,
  onLogout: PropTypes.func.isRequired,
  onStartASQ: PropTypes.func.isRequired,
}

export default FixedBar;
